import './TreeViewAssets.css';
import React, { FC, useEffect, useState } from 'react';
import cx from "classnames";
import { observer } from "mobx-react-lite";
import sortBy from 'lodash/sortBy';
import { LeftPaneContext } from "../LeftPaneContext";
import { LeftPaneSelectedSection } from '../../../../constants';
import { Accordion } from "../Accordion";
import icons from '../../../../icons';

export enum AssetType {
  VARIABLE = 'variable',
  FUNCTION = 'function',
  ROBOT = 'robot',
}

export interface AssetParam {
  type: string;
  name: string;
}

const assetHeaders = {
  [AssetType.VARIABLE]: {
    title: 'Variables',
    icon: icons.setVariable,
    type: AssetType.VARIABLE
  },
  [AssetType.FUNCTION]: {
    title: 'Functions',
    icon: icons.functions,
    type: AssetType.FUNCTION
  },
  [AssetType.ROBOT]: {
    title: 'Robot Assets',
    icon: icons.robot,
    type: AssetType.ROBOT
  }
};

const AssetHeader = ({ data: { title, icon, type }, onClick }) => (
  <span className="asset-header" onClick={(event) => onClick(event, type)}>
    <i className={icon}></i>
    <span>{title}</span>
  </span>
);

export const TreeViewAssets: FC = observer(() => {
  const {
    library,
    openDocument,
    isRunning,
    selectedSection,
    setSelectedSection
  } = React.useContext(LeftPaneContext);

  const [selectedAssetSection, setSelectedAssetSection] = useState<AssetType>();
  const [variables, setVariables] = useState<any[]>([]);
  const [functions, setFunctions] = useState<any[]>([]);

  const currentApplication = library.currentApplication;

  useEffect(() => {
    setSelectedAssetSection(undefined);
    const inputs = library.configManager.inputs;
    const filteredVariables: any[] = [];
    const filteredFunctions: any[] = [];
    const currentApplication = library.currentApplication;
    const sortedParameters = sortBy(currentApplication.parameters.json, 'name');
    sortedParameters.forEach((param) => {
      if (param.type === 'const') {
        filteredVariables.push(param);
      } else if (param.type === 'input') {
        filteredVariables.push({...param, inputValue: inputs.find(input => input.uuid === param.inputUuid)?.name});
      } else if (param.type === 'lambda') {
        filteredFunctions.push(param);
      }
    });

    setVariables(filteredVariables);
    setFunctions(filteredFunctions);
  }, [currentApplication.parameters.parameters]);

  const handleClick = (event: any, assetType: AssetType, param?: AssetParam) => {
    if(library.currentApplication.isReadOnly()) { return; }

    event.stopPropagation();
    if (isRunning) {
      return;
    }

    setSelectedSection(LeftPaneSelectedSection.ASSETS);
    setSelectedAssetSection(assetType);
    const currentApplication = library.currentApplication;
    openDocument(currentApplication, assetType, param);
  };

  return (
    <>
      <Accordion
        title={<AssetHeader data={assetHeaders[AssetType.VARIABLE]} onClick={handleClick} />}
        id="variables"
        keepOpenState
        className={cx({
          'accordion--selected': selectedAssetSection === AssetType.VARIABLE && selectedSection === LeftPaneSelectedSection.ASSETS
        })}
      >
        {variables.map((param) => (
          <div
            key={param.name}
            onClick={(event) => handleClick(event, AssetType.VARIABLE, param)}
            className="asset-param"
          >
            <i className={cx({
              [icons.variableIndividual]: param.type === 'const',
              [icons.input]: param.type === 'input',
            })}></i>
            <span className="text-ellipsis">{param.name}</span>
          </div>
        ))}
      </Accordion>
      <Accordion
        title={<AssetHeader data={assetHeaders[AssetType.FUNCTION]} onClick={handleClick} />}
        id="functions"
        keepOpenState
        className={cx({
          'accordion--selected': selectedAssetSection === AssetType.FUNCTION && selectedSection === LeftPaneSelectedSection.ASSETS
        })}
      >
        {functions.map((param) => (
          <div key={param.name} onClick={(event) => handleClick(event, AssetType.FUNCTION, param)} className="asset-param">
            <i className={icons.functionIndividual}></i>
            <span>{param.name}</span>
          </div>
        ))}
      </Accordion>
      <Accordion
        title={<AssetHeader data={assetHeaders[AssetType.ROBOT]} onClick={handleClick} />}
        className={cx('robot-assets', {
          'accordion--selected': selectedAssetSection === AssetType.ROBOT && selectedSection === LeftPaneSelectedSection.ASSETS
        })}
      >
      </Accordion>
    </>
  );
});